import { c as _c } from "react/compiler-runtime";
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { captureMessage } from '@sentry/browser';
import { isEmpty, isNil } from 'ramda';
import { useHttpCalls } from '../api/useHttpCalls';
import type { Preset, PutPresetRequest, QueryIdRequestBody } from '../types/Api';
import type { QueryFilterObject } from '../types/QueryFilterObject';
import { objectHasEveryEqualPropOfAnother } from '../utils/objectHasEveryEqualPropOfAnother';
type FilterModel = QueryIdRequestBody['filters'];
type UseLoadPresetsProps = {
  gridCode?: string;
  presetInstanceId?: string;
  onApiError?: (error: FetchBaseQueryError) => void;
  queryModifier?: (filter: QueryFilterObject) => QueryFilterObject;
};
export const useLoadPresets = props => {
  const $ = _c(10);
  const t0 = props?.onApiError;
  let t1;
  if ($[0] !== props.gridCode || $[1] !== props.presetInstanceId || $[2] !== t0) {
    t1 = {
      gridCode: props.gridCode,
      presetInstanceId: props.presetInstanceId,
      onApiError: t0
    };
    $[0] = props.gridCode;
    $[1] = props.presetInstanceId;
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const http = useHttpCalls(t1);
  let t2;
  if ($[4] !== http || $[5] !== props) {
    t2 = async preset => {
      if (!props.queryModifier) {
        return;
      }
      const data = await http.getDataQuery(preset.dataQueryId);
      if (isNil(data) || isNil(data?.filters)) {
        return;
      }
      const staticFilters = props.queryModifier ? props.queryModifier({}) : {};
      const isAllStaticFilterPresent = objectHasEveryEqualPropOfAnother(staticFilters, data.filters);
      if (isAllStaticFilterPresent) {
        return;
      }
      const filters = {
        ...data.filters,
        ...staticFilters
      } as FilterModel;
      await http.createDataQuery({
        filters,
        order: data.order,
        smartSearch: data.smartSearch ?? ""
      }).then(async queryIdResponseBody => {
        if (!queryIdResponseBody) {
          return;
        }
        const newDataQueryId = queryIdResponseBody.dataQueryId;
        const presetData = await http.getPreset(preset.id);
        if (isNil(presetData) || isNil(presetData?.gridSettings) || isEmpty(presetData?.gridSettings) || isNil(presetData?.columnsSettings) || isEmpty(presetData?.columnsSettings)) {
          captureMessage("updatePreset", scope => scope.setLevel("error").setExtras({
            userDescription: "update custom preset from useLoadPresets",
            state: presetData
          }));
          return;
        }
        const modifiedPreset = {
          gridSettings: presetData.gridSettings,
          columnsSettings: presetData.columnsSettings,
          dataQueryId: newDataQueryId
        };
        http.updatePreset(preset.id, modifiedPreset)?.catch(_temp);
      });
    };
    $[4] = http;
    $[5] = props;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const handleUpdatePresetForCurrentQueryModifier = t2;
  let t3;
  if ($[7] !== handleUpdatePresetForCurrentQueryModifier || $[8] !== http) {
    t3 = () => http.getPresets().then(async response => {
      if (!response) {
        return;
      }
      await Promise.all([...response.map(preset_0 => handleUpdatePresetForCurrentQueryModifier(preset_0))]);
      return response;
    });
    $[7] = handleUpdatePresetForCurrentQueryModifier;
    $[8] = http;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const loadPresets = t3;
  return loadPresets;
};
function _temp() {}